import { ChangeEvent, ForwardedRef, forwardRef, memo, Ref, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { Button } from '@mui/material';
import { ButtonProps } from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';

import { SelectFileButtonRoot } from './SelectFileButton.styles';

interface SelectFileButtonProps extends Omit<ButtonProps, 'onChange' | 'type'> {
  title: string;
  accept?: string;
  tooltip?: string;
  onChange?: (files: Nullable<File[]>, e: ChangeEvent<HTMLInputElement>) => void;
  multiple?: boolean;
  linkRef?: Ref<HTMLLabelElement>;
}

function SelectFileButton(props: SelectFileButtonProps, ref: ForwardedRef<HTMLInputElement>) {
  const { title, accept, tooltip, onChange, id, multiple: isMultiple = false, linkRef, ...rest } = props;

  const uniqueId = useMemo(() => id ?? uuid(), [id]);
  const fileFieldRef = useRef<Nullable<HTMLInputElement>>(ref as unknown as HTMLInputElement);

  function onChangeHandler(e: ChangeEvent<HTMLInputElement>) {
    let files: Nullable<File[]> = null;

    if (e.target.files) {
      files = [];

      // convert FileList to File[]
      for (const file of e.target.files) {
        files.push(file);
      }
    }

    onChange?.(files, e);

    if (fileFieldRef.current) {
      fileFieldRef.current.value = '';
    }
  }

  return (
    <SelectFileButtonRoot>
      <input
        accept={accept}
        id={uniqueId}
        onChange={onChangeHandler}
        ref={fileFieldRef}
        multiple={isMultiple}
        type="file"
      />

      <label htmlFor={uniqueId} ref={linkRef}>
        <Button component="span" {...rest}>
          <Tooltip title={tooltip}>
            <span>{title}</span>
          </Tooltip>
        </Button>
      </label>
    </SelectFileButtonRoot>
  );
}

export default memo(forwardRef(SelectFileButton));
